import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import HomeService from "../../services/HomeService";
import {
    getImageUrl,
    loadScreenshotJigsaw,
} from "../../prototype/mobile-jigsaw/lib/api";
import { fetchHomeStoryWordPuzzleGridPreview } from "../browse-stories/storybookGridPreview";
import {
    getHomeStorySlidingJigsawLinks,
    getLocalizedStoryFromHomeStoryRo,
    pickStoryCoverImageUrl,
} from "../browse-stories/storyRoUtils";
import { WEB_AD_TUTOR_STORY_ID } from "./webAdShots";

const FONT = '"Segoe UI", "Helvetica Neue", Arial, "Noto Sans", sans-serif';
const INK = "#0F172A";
const MUTED = "#475569";
const ACCENT = "#0EA5E9";
const GREEN = "#3BA666";

function excerpt(text, max = 140) {
    const t = String(text || "").replace(/\s+/g, " ").trim();
    if (!t) return "";
    if (t.length <= max) return t;
    return `${t.slice(0, max - 1).trim()}…`;
}

function StoryCover({ src, size = 200, label, overlay }) {
    return (
        <Box
            sx={{
                width: size,
                height: size,
                borderRadius: 2,
                overflow: "hidden",
                position: "relative",
                flexShrink: 0,
                boxShadow: "0 14px 32px rgba(15,23,42,0.18)",
                border: "3px solid #fff",
                bgcolor: "#E2E8F0",
            }}
        >
            {src ? (
                <Box
                    component="img"
                    src={src}
                    alt=""
                    sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
            ) : (
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: MUTED,
                        fontFamily: FONT,
                        fontWeight: 700,
                        fontSize: 14,
                        px: 1,
                        textAlign: "center",
                    }}
                >
                    Story image
                </Box>
            )}
            {overlay}
            {label ? (
                <Box
                    sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        px: 1,
                        py: 0.5,
                        bgcolor: "rgba(15,23,42,0.72)",
                        color: "#F8FAFC",
                        fontFamily: FONT,
                        fontWeight: 700,
                        fontSize: 12,
                        textAlign: "center",
                    }}
                >
                    {label}
                </Box>
            ) : null}
        </Box>
    );
}

function JigsawDieOverlay({ template }) {
    const pieces = template?.piecePaths?.pieces;
    if (Array.isArray(pieces) && pieces.length > 0) {
        return (
            <Box
                component="svg"
                sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                    zIndex: 1,
                }}
                viewBox="0 0 1536 1536"
                preserveAspectRatio="none"
            >
                {pieces.map((piece, index) => (
                    <path
                        key={index}
                        d={piece.path}
                        fill="none"
                        stroke="rgba(255,255,255,0.8)"
                        strokeWidth="3"
                    />
                ))}
            </Box>
        );
    }
    return (
        <Box
            sx={{
                position: "absolute",
                inset: 0,
                backgroundImage:
                    "linear-gradient(rgba(255,255,255,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.35) 1px, transparent 1px)",
                backgroundSize: "16.66% 16.66%",
                pointerEvents: "none",
                zIndex: 1,
            }}
        />
    );
}

function MiniWordGrid({ preview, size = 160 }) {
    if (!preview?.grid?.length) return null;
    const grid = preview.grid;
    const filled = new Set();
    (preview.filledCoordinates || []).forEach((coord) => {
        (coord.positions || []).forEach((p) => filled.add(`${p.row},${p.col}`));
    });
    const n = grid.length;
    const cell = Math.floor(size / n);
    return (
        <Box
            sx={{
                width: cell * n,
                height: cell * n,
                display: "grid",
                gridTemplateColumns: `repeat(${n}, ${cell}px)`,
                gridTemplateRows: `repeat(${n}, ${cell}px)`,
                gap: "1px",
                bgcolor: INK,
                border: `2px solid ${INK}`,
                boxShadow: "0 10px 24px rgba(15,23,42,0.16)",
                flexShrink: 0,
            }}
        >
            {grid.flatMap((row, r) =>
                row.map((ch, c) => {
                    const on = filled.has(`${r},${c}`) || (ch && ch !== " ");
                    return (
                        <Box
                            key={`${r}-${c}`}
                            sx={{
                                width: cell,
                                height: cell,
                                bgcolor: on ? "#fff" : INK,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontFamily: FONT,
                                fontWeight: 800,
                                fontSize: Math.max(6, Math.round(cell * 0.4)),
                                color: INK,
                            }}
                        >
                            {on && ch && ch !== " " ? ch : ""}
                        </Box>
                    );
                }),
            )}
        </Box>
    );
}

function PuzzleTypeLabel({ children }) {
    return (
        <Typography
            sx={{
                fontFamily: FONT,
                fontWeight: 800,
                fontSize: 13,
                color: MUTED,
                textAlign: "center",
                mb: 0.75,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
            }}
        >
            {children}
        </Typography>
    );
}

/**
 * Tutor creatives from story {@link WEB_AD_TUTOR_STORY_ID} ("The Gallery Deception").
 * Variants: content (story image + text) · make (image → puzzles) · print (paper + play online).
 */
export default function WebAdTutorBoard({ variant = "content" }) {
    const [row, setRow] = useState(null);
    const [coverOverride, setCoverOverride] = useState("");
    const [gridPreview, setGridPreview] = useState(null);
    const [jigsawTemplate, setJigsawTemplate] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await HomeService.getStoryById(WEB_AD_TUTOR_STORY_ID);
                if (cancelled) return;
                if (!data) {
                    setFailed(true);
                    return;
                }
                setRow(data);
                const [preview, jigsawPack] = await Promise.all([
                    fetchHomeStoryWordPuzzleGridPreview(data).catch(() => null),
                    loadScreenshotJigsaw({
                        storyId: WEB_AD_TUTOR_STORY_ID,
                        expectedGrid: 6,
                    }).catch(() => null),
                ]);
                if (cancelled) return;
                setGridPreview(preview);
                setJigsawTemplate(jigsawPack?.template || null);
                if (!pickStoryCoverImageUrl(data) && jigsawPack?.group?.story) {
                    const fromJigsaw = getImageUrl(jigsawPack.group.story);
                    if (fromJigsaw) setCoverOverride(fromJigsaw);
                }
            } catch {
                if (!cancelled) setFailed(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const localized = useMemo(
        () => getLocalizedStoryFromHomeStoryRo(row, "english"),
        [row],
    );
    const coverUrl = useMemo(() => {
        if (!row) return coverOverride || "";
        return pickStoryCoverImageUrl(row) || localized.imageUrl || coverOverride || "";
    }, [row, localized.imageUrl, coverOverride]);
    const title = localized.title || "The Gallery Deception";
    const text = excerpt(localized.text, variant === "content" ? 160 : 90);
    const links = useMemo(() => getHomeStorySlidingJigsawLinks(row), [row]);
    const hasJigsaw = Boolean(links.jigsawPlayId);
    const puzzleType =
        gridPreview?.type === "WORD_SEARCH"
            ? "Word search"
            : gridPreview?.type === "HIDDEN_MESSAGE"
              ? "Hidden message"
              : gridPreview
                ? "Crossword"
                : null;

    if (failed) {
        return (
            <Box sx={{ color: MUTED, fontFamily: FONT, fontWeight: 600, fontSize: 14 }}>
                Could not load story
            </Box>
        );
    }

    if (!row) {
        return (
            <Box sx={{ width: 280, height: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress size={36} sx={{ color: GREEN }} />
            </Box>
        );
    }

    if (variant === "content") {
        return (
            <Box
                data-web-ad-tutor="content"
                sx={{ display: "flex", alignItems: "center", gap: 2.5, maxWidth: 560 }}
            >
                <StoryCover src={coverUrl} size={220} label="Your content" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT,
                            fontWeight: 800,
                            fontSize: 22,
                            color: INK,
                            lineHeight: 1.2,
                            mb: 1,
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: FONT,
                            fontWeight: 600,
                            fontSize: 15,
                            color: MUTED,
                            lineHeight: 1.35,
                        }}
                    >
                        {text || "Photos, words, or a theme you love"}
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (variant === "make") {
        return (
            <Box
                data-web-ad-tutor="make"
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <PuzzleTypeLabel>Story</PuzzleTypeLabel>
                    <StoryCover src={coverUrl} size={150} />
                </Box>
                <Typography sx={{ fontFamily: FONT, fontWeight: 900, fontSize: 36, color: ACCENT }}>
                    →
                </Typography>
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
                    {gridPreview ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <PuzzleTypeLabel>{puzzleType}</PuzzleTypeLabel>
                            <MiniWordGrid preview={gridPreview} size={150} />
                        </Box>
                    ) : null}
                    {hasJigsaw || coverUrl ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <PuzzleTypeLabel>Jigsaw</PuzzleTypeLabel>
                            <StoryCover
                                src={coverUrl}
                                size={150}
                                overlay={<JigsawDieOverlay template={jigsawTemplate} />}
                            />
                        </Box>
                    ) : null}
                </Box>
            </Box>
        );
    }

    // print
    return (
        <Box
            data-web-ad-tutor="print"
            sx={{ display: "flex", alignItems: "center", gap: 3 }}
        >
            <Box
                sx={{
                    width: 200,
                    height: 260,
                    bgcolor: "#fff",
                    borderRadius: 1,
                    border: "2px solid #CBD5E1",
                    boxShadow: "0 12px 28px rgba(15,23,42,0.14)",
                    p: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    boxSizing: "border-box",
                }}
            >
                <Box
                    component="img"
                    src={coverUrl}
                    alt=""
                    sx={{
                        width: "100%",
                        height: 100,
                        objectFit: "cover",
                        borderRadius: 1,
                        bgcolor: "#E2E8F0",
                    }}
                />
                <Typography
                    sx={{
                        fontFamily: FONT,
                        fontWeight: 800,
                        fontSize: 13,
                        color: INK,
                        lineHeight: 1.2,
                    }}
                >
                    {title}
                </Typography>
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {gridPreview ? (
                        <MiniWordGrid preview={gridPreview} size={110} />
                    ) : (
                        <StoryCover
                            src={coverUrl}
                            size={100}
                            overlay={<JigsawDieOverlay template={jigsawTemplate} />}
                        />
                    )}
                </Box>
                <Typography
                    sx={{
                        fontFamily: FONT,
                        fontWeight: 700,
                        fontSize: 10,
                        color: MUTED,
                        textAlign: "center",
                    }}
                >
                    Print worksheet
                </Typography>
            </Box>
            <Typography sx={{ fontFamily: FONT, fontWeight: 900, fontSize: 32, color: "#94A3B8" }}>
                +
            </Typography>
            <Box
                sx={{
                    width: 120,
                    height: 220,
                    borderRadius: 4,
                    bgcolor: INK,
                    p: 1,
                    boxShadow: "0 16px 36px rgba(15,23,42,0.28)",
                    boxSizing: "border-box",
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        borderRadius: 3,
                        bgcolor: "#ECFDF5",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        px: 1,
                        overflow: "hidden",
                    }}
                >
                    <Box
                        component="img"
                        src={coverUrl}
                        alt=""
                        sx={{
                            width: 72,
                            height: 72,
                            objectFit: "cover",
                            borderRadius: 1,
                            border: "2px solid #fff",
                            boxShadow: "0 4px 12px rgba(15,23,42,0.15)",
                        }}
                    />
                    <Typography
                        sx={{
                            fontFamily: FONT,
                            fontWeight: 800,
                            fontSize: 12,
                            color: GREEN,
                            textAlign: "center",
                            lineHeight: 1.2,
                        }}
                    >
                        Play online
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
